import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { coursesAPI } from '../services/api';
import api from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import EmptyState from '../components/ui/EmptyState';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import { getCreatableCourseTypes } from '../utils/coursePermissions';
import { getCourseDomainStyle } from '../utils/courseDomain';
import { useToast } from '../context/ToastContext';

const CourseContent = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [finalTest, setFinalTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Live tracking pentru bara de completitudine: durata e citită din input-ul copilului în
  // timp real (nu doar din `course.duration` care se actualizează după save). Așa bifa verde
  // apare imediat ce userul a tipat o valoare validă, nu după ce a apăsat „Salvează și finalizează".
  const [liveDurationFilled, setLiveDurationFilled] = useState(false);
  // Confirmare la „Înapoi la cursuri" — cursul a fost deja creat în DB la pasul anterior
  // (CourseForm trimite POST la create). Dacă userul iese fără să configureze nimic, vrem să-l
  // întrebăm dacă șterge draft-ul. Dacă a configurat conținut, doar avertizăm despre modificări
  // nesalvate.
  const [showBackModal, setShowBackModal] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  // Ref către CourseDetailsTab — expune validate() (cu scroll+chenar roșu pe câmpurile invalide)
  // și save() async. Folosit de butonul „Salvează și finalizează" — nu mai există buton separat
  // de save în tab-ul Detalii.
  const detailsRef = useRef(null);
  // Flag ca să inițializăm `liveDurationFilled` doar la primul fetch (de la server). Refetch-urile
  // ulterioare (după add modul / save test / etc.) NU trebuie să suprascrie state-ul live —
  // userul poate avea valoarea tipată în input fără să fi salvat încă cursul, iar
  // `course.duration` de la server e încă null. Sursa de adevăr după primul fetch e callback-ul
  // `onDurationChange` apelat de copil la onChange pe input.
  const liveDurationInitializedRef = useRef(false);

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    try {
      const [courseRes, modulesRes, finalTestRes] = await Promise.all([
        coursesAPI.getById(courseId),
        api.get(`/modules/${courseId}`),
        api.get(`/courses/${courseId}/final-test`).catch(() => ({ data: null })),
      ]);
      setCourse(courseRes.data);
      setModules(modulesRes.data);
      setFinalTest(finalTestRes.data);
      // Inițializează indicatorul live DOAR la primul fetch. Refetch-urile ulterioare (după
      // add modul, save test etc.) păstrează state-ul live setat de copil prin onDurationChange —
      // altfel, când userul tipează durata fără să o salveze și adaugă un material, fetchData
      // resetează bifa la valoarea din DB (încă null), iar bara devine portocalie din nou.
      if (!liveDurationInitializedRef.current) {
        setLiveDurationFilled(!!courseRes.data?.duration);
        liveDurationInitializedRef.current = true;
      }
    } catch (err) {
      showToast(t('courseContent.loadDataError'), 'error');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  // Returnează modulul salvat (necesar pentru a-i atașa materiale pending la create).
  // Nu mai închide modalul automat — ModuleModal îl închide după ce upload-urile sunt gata.
  const handleSaveModule = async (formData) => {
    if (editingModule) {
      const res = await api.put(`/modules/${editingModule.id}`, formData);
      return res.data;
    }
    const res = await api.post('/modules', { ...formData, courseId });
    return res.data;
  };

  const handleModuleModalClose = async () => {
    await fetchData();
    setShowModuleModal(false);
    setEditingModule(null);
  };

  // Confirmare unificată pentru ștergere modul/întrebare/răspuns
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // { kind: 'module'|'question'|'answer', id, title, message }

  const handleDeleteModule = (moduleId) =>
    setDeleteConfirm({
      kind: 'module',
      id: moduleId,
      title: t('courseContent.deleteModuleTitle'),
      message: t('courseContent.deleteModuleMsg'),
    });

  const handleSaveQuestion = async (formData) => {
    try {
      if (editingQuestion) {
        await api.put(`/modules/questions/${editingQuestion.id}`, formData);
      } else {
        await api.post(`/modules/${selectedModuleId}/questions`, formData);
      }
      await fetchData();
      setShowQuestionModal(false);
      setEditingQuestion(null);
    } catch (err) {
      showToast(t('courseContent.saveQuestionError'), 'error');
    }
  };

  const handleDeleteQuestion = (questionId) =>
    setDeleteConfirm({
      kind: 'question',
      id: questionId,
      title: t('courseContent.deleteQuestionTitle'),
      message: t('courseContent.deleteQuestionMsg'),
    });

  const handleSaveAnswer = async (formData) => {
    try {
      if (editingAnswer) {
        await api.put(`/modules/answers/${editingAnswer.id}`, formData);
      } else {
        await api.post(`/modules/questions/${selectedQuestionId}/answers`, formData);
      }
      await fetchData();
      setShowAnswerModal(false);
      setEditingAnswer(null);
    } catch (err) {
      showToast(t('courseContent.saveAnswerError'), 'error');
    }
  };

  const handleDeleteAnswer = (answerId) =>
    setDeleteConfirm({
      kind: 'answer',
      id: answerId,
      title: t('courseContent.deleteAnswerTitle'),
      message: t('courseContent.deleteAnswerMsg'),
    });

  const [deletingItem, setDeletingItem] = useState(false);
  const runDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingItem(true);
    try {
      const { kind, id } = deleteConfirm;
      const urls = {
        module: `/modules/${id}`,
        question: `/modules/questions/${id}`,
        answer: `/modules/answers/${id}`,
      };
      const errors = {
        module: t('courseContent.deleteItemError.material'),
        question: t('courseContent.deleteItemError.question'),
        answer: t('courseContent.deleteItemError.answer'),
      };
      try {
        await api.delete(urls[kind]);
        setDeleteConfirm(null);
        await fetchData();
      } catch (err) {
        showToast(errors[kind], 'error');
      }
    } finally {
      setDeletingItem(false);
    }
  };

  if (loading) return <Spinner label={t('courseContent.loadingContent')} />;

  const hasFinalTest = finalTest && finalTest.questions && finalTest.questions.length > 0;
  // Diferențiere vizuală pe domeniu: SSM albastru / SU roșu (tentă ușoară + badge lângă titlu).
  const domainStyle = getCourseDomainStyle(course?.type);

  // Verificări de completitudine — partajate între indicatorul de sus (clickabil) și pop-up-ul „Finalizează".
  // „Timp minim pe curs" e opțional, deci NU intră aici. Durata apare în bară ca status vizual
  // (verde dacă salvată, roșu dacă lipsă). Validarea LIVE pe durata (chenar roșu pe câmp,
  // detectează ce-a tipat user-ul în input chiar înainte de save) e separată în `CourseDetailsTab`.
  const completenessChecks = [
    { label: t('courseContent.completenessModulesAdded'), ok: modules.length > 0, tab: 'modules' },
    // Durata: bifa verde apare LIVE (din `liveDurationFilled`, actualizat de copil la onChange).
    // Click pe item scrollează direct la input-ul duratei (scrollTarget: 'duration').
    {
      label: t('courseContent.completenessDurationSheet'),
      ok: liveDurationFilled,
      tab: 'details',
      scrollTarget: 'duration',
    },
    ...(course?.requiresTest
      ? [{ label: t('courseContent.completenessFinalTest'), ok: hasFinalTest, tab: 'finaltest' }]
      : []),
  ];
  const allComplete = completenessChecks.every((c) => c.ok);
  const missingChecks = completenessChecks.filter((c) => !c.ok);
  const goToSection = async (tab, scrollTarget) => {
    setActiveTab(tab);
    // Așteaptă un tick ca React să randeze tab-ul (CourseDetailsTab e cu `hidden` când nu e activ).
    if (scrollTarget === 'duration') {
      await new Promise((r) => setTimeout(r, 80));
      detailsRef.current?.scrollToDuration?.();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  // Draft = curs care abia a fost creat la pasul „Curs nou" (titlu + tip), fără conținut.
  // Folosit pentru a oferi opțiunea de ștergere la „Înapoi la cursuri".
  const isDraft = !course?.duration && modules.length === 0 && !hasFinalTest;
  const backTargetPath = course?.isTemplate ? '/courses?tab=templates' : '/courses';
  // Pentru mesajul modalului, EXCLUDEM Durata — modalul se deschide doar după ce `validate()`
  // copilului confirmă că durata e completată (citește live din DOM). Așa nu mai apare „Ai uitat
  // durata" în modal dacă userul a tipat valoarea dar nu a apucat să salveze.
  const missingForModal = missingChecks.filter(
    (c) => c.label !== t('courseContent.completenessDurationSheet')
  );
  const finishMessage =
    missingForModal.length > 0
      ? t('courseContent.finishMessageMissing', {
          items: missingForModal.map((c) => c.label).join(', '),
        })
      : t('courseContent.finishMessageComplete');

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          course.isTemplate
            ? { label: t('courseContent.templatesLibraryCrumb'), href: '/courses?tab=templates' }
            : { label: t('courses.title'), href: '/courses' },
          { label: course.title || t('courseContent.courseFallback') },
          { label: t('courses.content') },
        ]}
      />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className={`rounded-lg px-4 py-3 ${domainStyle.cardTint}`}>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mb-1.5 ${domainStyle.badgeClass}`}
          >
            {domainStyle.domain}
          </span>
          <h1 className="page-title">{course.title}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('courseContent.pageSubtitle')}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {activeTab === 'modules' && (
            <Button
              onClick={() => {
                setEditingModule(null);
                setShowModuleModal(true);
              }}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('courseContent.addMaterial')}
            </Button>
          )}
          {/* Buton unic „Salvează și finalizează": validează durata (scroll + chenar roșu dacă lipsește),
              apoi deschide modalul de confirmare. La confirmare → save automat + navigate. Nu mai
              există buton separat „Salvează detalii" — totul prin acest singur buton.
              Bug anterior: verificam `course?.duration` aici (stale, neresalvat) + child-ul verifica
              `form.durationValue` (live) → check-ul de aici greșea când userul TIPĂREA dar nu salvase.
              Acum delegăm complet validarea către child via `detailsRef.current.validate()`. */}
          <Button
            variant="primary"
            onClick={async () => {
              // Asigură că suntem pe tab-ul Detalii înainte de a valida (ca să fie randat input-ul).
              if (activeTab !== 'details') {
                setActiveTab('details');
                // Așteaptă un tick pentru ca React să randeze tab-ul + input-ul cu ref.
                await new Promise((r) => setTimeout(r, 100));
              }
              const valid = detailsRef.current?.validate?.();
              if (!valid) return;
              // Blocaj test obligatoriu — pentru IIG/ILM (sau orice curs cu requiresTest=true),
              // nu se poate finaliza fără test configurat cu cel puțin o întrebare.
              if (course?.requiresTest && !hasFinalTest) {
                showToast(t('courseContent.testRequiredError'), 'error');
                goToSection('finaltest');
                return;
              }
              setShowFinishModal(true);
            }}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t('courseContent.saveAndFinalize')}
          </Button>
          {/* Singur buton: „Înapoi la cursuri/template-uri" în funcție de context.
              Deschide un ConfirmModal — pentru draft (curs fără conținut) oferă ștergerea,
              pentru curs cu conținut avertizează despre modificări nesalvate. */}
          <Button variant="ghost" onClick={() => setShowBackModal(true)}>
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {course?.isTemplate
              ? t('courseContent.backToTemplates')
              : t('courseContent.backToCourses')}
          </Button>
        </div>
      </div>

      {/* #6: indicator de completitudine — fiecare element e clickabil și te duce la secțiunea de configurat. */}
      {!allComplete && (
        <div className="flex items-center gap-4 px-4 py-3 bg-amber-50 dark:bg-slate-800/60 border border-amber-200 dark:border-slate-700 rounded-lg text-sm">
          <svg
            className="w-5 h-5 text-amber-500 dark:text-amber-300 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {completenessChecks.map((c, i) => (
              <button
                type="button"
                key={i}
                onClick={() => goToSection(c.tab, c.scrollTarget)}
                title={c.ok ? c.label : t('courseContent.configurePrefix', { label: c.label })}
                className={`flex items-center gap-1.5 hover:underline cursor-pointer ${c.ok ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300 font-medium'}`}
              >
                {c.ok ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 4h.01" />
                  </svg>
                )}
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {['details', 'modules', 'finaltest'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600 dark:text-primary-300'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <span className="inline-flex items-center gap-2">
              {tab === 'details' && (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  {t('courseContent.tabs.details')}
                </>
              )}
              {tab === 'modules' && (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  {t('courseContent.modulesCount', { count: modules.length })}
                </>
              )}
              {tab === 'finaltest' && (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                  {t('courseContent.tabs.finalTest')}
                  {hasFinalTest ? (
                    <Badge color="green">
                      {t('courseContent.finalTestConfigured', {
                        count: finalTest.questions.length,
                      })}
                    </Badge>
                  ) : (
                    <Badge color="red">{t('courseContent.notConfigured')}</Badge>
                  )}
                </>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* TAB DETALII — layout aerat, vertical, similar cu TemplateDetails */}
      <div className={activeTab === 'details' ? '' : 'hidden'}>
        <div className="mb-4 p-3 bg-blue-50 dark:bg-slate-800/60 border border-blue-200 dark:border-slate-700 rounded-lg text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
          <svg
            className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <strong>{t('courseContent.versioningLabel')}</strong>{' '}
            {t('courseContent.versioningBody')}
          </div>
        </div>
        <CourseDetailsTab
          ref={detailsRef}
          course={course}
          onDurationChange={setLiveDurationFilled}
          onContinue={() => {
            setActiveTab('modules');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onSave={async (data) => {
            try {
              const { isTemplate, ...rest } = data;
              await coursesAPI.update(courseId, rest);
              if (typeof isTemplate === 'boolean' && isTemplate !== course?.isTemplate) {
                try {
                  await coursesAPI.toggleTemplate(courseId, isTemplate);
                } catch (_) {}
              }
              showToast(t('courseContent.detailsSaved'), 'success');
              // Re-fetch ca să reflectăm valorile curente în card + celelalte tab-uri.
              // Nu navigăm — user decide când pleacă (buton „Înapoi la cursuri" sus).
              fetchData();
            } catch (err) {
              showToast(t('courseContent.saveDetailsError'), 'error');
            }
          }}
        />
      </div>

      {/* TAB MODULE */}
      {activeTab === 'modules' && (
        <div className="space-y-4">
          {modules.length === 0 ? (
            <EmptyState
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              }
              title={t('courseContent.noModules')}
              description={t('courseContent.noModulesDesc')}
              actionLabel={t('courseContent.createFirstMaterialBtn')}
              onAction={() => {
                setEditingModule(null);
                setShowModuleModal(true);
              }}
            />
          ) : (
            modules.map((module, index) => (
              <Card key={module.id}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge color="blue">
                        {t('courseContent.materialBadge', { index: index + 1 })}
                      </Badge>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        {module.title}
                      </h3>
                    </div>
                    {module.videoUrl && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        {t('courseContent.videoPrefix', { url: module.videoUrl })}
                      </p>
                    )}
                    {module.materials?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {module.materials.filter((m) => m.kind === 'PDF').length > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                              />
                            </svg>
                            {module.materials.filter((m) => m.kind === 'PDF').length} PDF
                          </span>
                        )}
                        {module.materials.filter((m) => m.kind === 'POWERPOINT').length > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M16.5 3.75h.008v.008H16.5V3.75z"
                              />
                            </svg>
                            {module.materials.filter((m) => m.kind === 'POWERPOINT').length}{' '}
                            PowerPoint
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Reorder buttons */}
                    <button
                      onClick={async () => {
                        if (index === 0) return;
                        const prev = modules[index - 1];
                        await Promise.all([
                          api.put(`/modules/${module.id}`, { orderIndex: prev.orderIndex }),
                          api.put(`/modules/${prev.id}`, { orderIndex: module.orderIndex }),
                        ]);
                        await fetchData();
                      }}
                      disabled={index === 0}
                      className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('courseContent.moveUp')}
                    >
                      <svg
                        className="w-4 h-4 text-slate-500 dark:text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={async () => {
                        if (index === modules.length - 1) return;
                        const next = modules[index + 1];
                        await Promise.all([
                          api.put(`/modules/${module.id}`, { orderIndex: next.orderIndex }),
                          api.put(`/modules/${next.id}`, { orderIndex: module.orderIndex }),
                        ]);
                        await fetchData();
                      }}
                      disabled={index === modules.length - 1}
                      className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('courseContent.moveDown')}
                    >
                      <svg
                        className="w-4 h-4 text-slate-500 dark:text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setEditingModule(module);
                        setShowModuleModal(true);
                      }}
                    >
                      <svg
                        className="w-3.5 h-3.5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteModule(module.id)}
                    >
                      <svg
                        className="w-3.5 h-3.5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>

                <div className="ml-4 border-l-2 border-slate-200 dark:border-slate-800 pl-4 space-y-3">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-4">
                      <h4 className="font-semibold text-slate-700 dark:text-slate-300">
                        {t('courseContent.questionsCount', { count: module.questions.length })}
                      </h4>
                      {module.questions.length > 0 && (
                        <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={module.showCorrectAnswers !== false}
                            onChange={async (e) => {
                              await api.put(`/modules/${module.id}`, {
                                showCorrectAnswers: e.target.checked,
                              });
                              await fetchData();
                            }}
                            className="w-3.5 h-3.5 text-primary-600 rounded border-slate-300 dark:border-slate-700"
                          />
                          {t('courseContent.showCorrectAnswersLabel')}
                        </label>
                      )}
                    </div>
                    <Button
                      variant="accent"
                      size="sm"
                      onClick={() => {
                        setSelectedModuleId(module.id);
                        setEditingQuestion(null);
                        setShowQuestionModal(true);
                      }}
                    >
                      <svg
                        className="w-3.5 h-3.5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      {t('courseContent.addQuestion')}
                    </Button>
                  </div>

                  {module.questions.map((question, qIndex) => (
                    <div key={question.id} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                            Q{qIndex + 1}
                          </span>
                          <p className="font-medium text-slate-900 dark:text-slate-100 mt-1">
                            {question.text}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingQuestion(question);
                              setSelectedModuleId(module.id);
                              setShowQuestionModal(true);
                            }}
                            className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                            title={t('common.edit')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-1.5 text-danger-500 dark:text-danger-300 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-950/50 rounded transition-colors"
                            title={t('common.delete')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="ml-4 space-y-2 mt-3">
                        {question.answers.map((answer, aIndex) => (
                          <div
                            key={answer.id}
                            className={`flex justify-between items-center p-2 rounded ${
                              answer.isCorrect
                                ? 'bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700'
                                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                                {String.fromCharCode(65 + aIndex)}
                              </span>
                              {answer.isCorrect && (
                                <svg
                                  className="w-4 h-4 text-accent-600 dark:text-accent-300"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                              <span className="text-sm">{answer.text}</span>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setEditingAnswer(answer);
                                  setSelectedQuestionId(question.id);
                                  setShowAnswerModal(true);
                                }}
                                className="p-1 text-slate-500 dark:text-slate-400 hover:text-slate-700 rounded transition-colors"
                                title={t('common.edit')}
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteAnswer(answer.id)}
                                className="p-1 text-danger-500 dark:text-danger-300 hover:text-danger-700 rounded transition-colors"
                                title={t('common.delete')}
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            setSelectedQuestionId(question.id);
                            setEditingAnswer(null);
                            setShowAnswerModal(true);
                          }}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded hover:border-slate-400 text-sm text-slate-600 dark:text-slate-300 transition-colors"
                        >
                          {t('courseContent.addAnswer')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* TAB TEST FINAL */}
      {activeTab === 'finaltest' && (
        <FinalTestEditor
          courseId={courseId}
          finalTest={finalTest}
          onSaved={(updated) => setFinalTest(updated)}
        />
      )}

      {showModuleModal && (
        <ModuleModal
          module={editingModule}
          onSave={handleSaveModule}
          onDone={handleModuleModalClose}
          onClose={() => {
            setShowModuleModal(false);
            setEditingModule(null);
          }}
        />
      )}
      {showQuestionModal && (
        <QuestionModal
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onClose={() => {
            setShowQuestionModal(false);
            setEditingQuestion(null);
          }}
        />
      )}
      {showAnswerModal && (
        <AnswerModal
          answer={editingAnswer}
          onSave={handleSaveAnswer}
          onClose={() => {
            setShowAnswerModal(false);
            setEditingAnswer(null);
          }}
        />
      )}

      {/* Confirmare finalizare cu save integrat. „Da" salvează detaliile + navighează la lista
          de cursuri; „Nu" închide modalul (rămâne pe pagină ca să mai modifice). Avertizează dacă
          mai lipsesc piese (module, test final). */}
      <ConfirmModal
        open={showFinishModal}
        onClose={() => !finalizing && setShowFinishModal(false)}
        onConfirm={async () => {
          setFinalizing(true);
          try {
            await detailsRef.current?.save?.();
            setShowFinishModal(false);
            navigate('/courses');
          } catch (err) {
            // toast-ul de eroare e afișat în onSave (din parent). Lăsăm modalul deschis.
          } finally {
            setFinalizing(false);
          }
        }}
        title={t('courseContent.finishModalTitle')}
        message={finishMessage}
        confirmLabel={finalizing ? t('common.saving') : t('courseContent.finishConfirmLabel')}
        cancelLabel={t('courseContent.finishCancelLabel')}
        variant={missingForModal.length > 0 ? 'danger' : 'primary'}
        loading={finalizing}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={runDelete}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        loading={deletingItem}
      />

      {/* Confirmare la „Înapoi la cursuri". Două scenarii:
          - Draft (curs creat acum, fără durată / materiale / test final) → oferim ștergerea
            cursului ca să nu rămână rebut în listă.
          - Curs cu conținut → doar avertizăm că pierde modificări nesalvate din tab-ul Detalii
            (durata, descrierea etc. nu se salvează decât prin „Salvează și finalizează"). */}
      <ConfirmModal
        open={showBackModal}
        onClose={() => !deletingDraft && setShowBackModal(false)}
        onConfirm={async () => {
          if (isDraft) {
            setDeletingDraft(true);
            try {
              await coursesAPI.delete(courseId);
              setShowBackModal(false);
              navigate(backTargetPath);
            } catch (err) {
              showToast(t('courseContent.deleteCourseError'), 'error');
            } finally {
              setDeletingDraft(false);
            }
          } else {
            setShowBackModal(false);
            navigate(backTargetPath);
          }
        }}
        title={isDraft ? t('courseContent.deleteDraftTitle') : t('courseContent.closeUnsavedTitle')}
        message={isDraft ? t('courseContent.deleteDraftMsg') : t('courseContent.closeUnsavedMsg')}
        confirmLabel={
          deletingDraft
            ? t('courseContent.deleting')
            : isDraft
              ? t('courseContent.deleteDraftConfirm')
              : t('courseContent.closeAnywayConfirm')
        }
        cancelLabel={
          isDraft ? t('courseContent.deleteDraftCancel') : t('courseContent.closeUnsavedCancel')
        }
        variant="danger"
        loading={deletingDraft}
      />
    </div>
  );
};

// Editor complet pentru Test Final -- salveaza in FinalTest/FinalTestQuestion
const FinalTestEditor = ({ courseId, finalTest, onSaved }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [passingScore, setPassingScore] = useState(finalTest?.passingScore || 80);
  const [questions, setQuestions] = useState(
    finalTest?.questions?.map((q) => ({
      text: q.text,
      type: q.type || 'single_choice',
      answers: q.answers?.map((a) => ({ text: a.text, isCorrect: a.isCorrect })) || [],
    })) || []
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        text: '',
        type: 'single_choice',
        answers: [
          { text: '', isCorrect: true },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
        ],
      },
    ]);
  };

  const removeQuestion = (qIdx) => {
    setQuestions(questions.filter((_, i) => i !== qIdx));
  };

  const updateQuestion = (qIdx, field, value) => {
    const updated = [...questions];
    updated[qIdx] = { ...updated[qIdx], [field]: value };
    setQuestions(updated);
  };

  const addAnswer = (qIdx) => {
    const updated = [...questions];
    updated[qIdx] = {
      ...updated[qIdx],
      answers: [...updated[qIdx].answers, { text: '', isCorrect: false }],
    };
    setQuestions(updated);
  };

  const removeAnswer = (qIdx, aIdx) => {
    const updated = [...questions];
    updated[qIdx] = {
      ...updated[qIdx],
      answers: updated[qIdx].answers.filter((_, i) => i !== aIdx),
    };
    setQuestions(updated);
  };

  const updateAnswer = (qIdx, aIdx, field, value) => {
    const updated = [...questions];
    const question = { ...updated[qIdx] };
    const answers = [...question.answers];

    if (field === 'isCorrect' && value === true && question.type === 'single_choice') {
      // Single choice: deselecteaza celelalte
      question.answers = answers.map((a, i) => ({ ...a, isCorrect: i === aIdx }));
    } else {
      answers[aIdx] = { ...answers[aIdx], [field]: value };
      question.answers = answers;
    }
    updated[qIdx] = question;
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (questions.length === 0) {
      showToast(t('courseContent.addAtLeastOneQuestion'), 'error');
      return;
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.text.trim()) {
        showToast(t('courseContent.questionNoText', { num: i + 1 }), 'error');
        return;
      }
      if (q.answers.length < 2) {
        showToast(t('courseContent.questionMinAnswers', { num: i + 1 }), 'error');
        return;
      }
      if (!q.answers.some((a) => a.isCorrect)) {
        showToast(t('courseContent.questionNoCorrect', { num: i + 1 }), 'error');
        return;
      }
      for (let j = 0; j < q.answers.length; j++) {
        if (!q.answers[j].text.trim()) {
          showToast(t('courseContent.answerEmpty', { aNum: j + 1, qNum: i + 1 }), 'error');
          return;
        }
      }
    }

    setSaving(true);
    try {
      const res = await api.post(`/courses/${courseId}/final-test`, { passingScore, questions });
      onSaved(res.data);
      showToast(
        t('courseContent.finalTestSaved', { count: questions.length, score: passingScore }),
        'success'
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      showToast(err.response?.data?.error || t('courseContent.saveTestError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header cu setari */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {t('courseContent.finalTestMandatory')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('courseContent.finalTestDesc')}
            </p>
          </div>
          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800 px-4 py-3 rounded-lg">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
              {t('courseContent.minScore')}
            </label>
            <input
              type="number"
              min="50"
              max="100"
              value={passingScore}
              onChange={(e) => setPassingScore(parseInt(e.target.value))}
              className="w-20 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-center font-bold text-primary-600 dark:text-primary-300 focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
            <span className="text-slate-500 dark:text-slate-400 font-medium">%</span>
          </div>
        </div>
      </Card>

      {questions.length === 0 && (
        <div className="card card-padding border-2 border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/50 text-center py-10">
          <div className="w-12 h-12 rounded-lg bg-warning-100 dark:bg-warning-950/50 flex items-center justify-center mx-auto mb-4 text-warning-600 dark:text-warning-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
          </div>
          <p className="text-warning-800 dark:text-warning-300 font-semibold text-lg">
            {t('courseContent.finalTestNotConfigured')}
          </p>
          <p className="text-warning-700 dark:text-warning-300 text-sm mt-2 mb-4">
            {t('courseContent.finalTestNotConfiguredDesc')}
          </p>
          <Button
            onClick={addQuestion}
            className="bg-warning-600 hover:bg-warning-700 text-white border-0"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('courseContent.addFirstQuestion')}
          </Button>
        </div>
      )}

      {questions.map((question, qIdx) => (
        <Card key={qIdx} className="border-2">
          <div className="flex items-start gap-4 mb-5">
            <div className="flex-shrink-0 w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
              {qIdx + 1}
            </div>
            <div className="flex-1">
              <textarea
                value={question.text}
                onChange={(e) => updateQuestion(qIdx, 'text', e.target.value)}
                placeholder={t('courseContent.questionPlaceholder')}
                rows={2}
                className="input resize-none text-base"
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                <select
                  value={question.type}
                  onChange={(e) => updateQuestion(qIdx, 'type', e.target.value)}
                  className="select text-sm"
                >
                  <option value="single_choice">{t('courseContent.singleChoice')}</option>
                  <option value="multiple_choice">{t('courseContent.multipleChoice')}</option>
                </select>
                <span className="text-xs text-slate-400 dark:text-slate-400">
                  {question.type === 'single_choice'
                    ? t('courseContent.singleChoiceHint')
                    : t('courseContent.multipleChoiceHint')}
                </span>
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => removeQuestion(qIdx)}
              className="flex-shrink-0"
            >
              <svg
                className="w-3.5 h-3.5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {t('common.delete')}
            </Button>
          </div>

          {/* Raspunsuri */}
          <div className="space-y-2" style={{ paddingLeft: '3.25rem' }}>
            {question.answers.map((answer, aIdx) => (
              <div
                key={aIdx}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition ${
                  answer.isCorrect
                    ? 'border-accent-400 dark:border-accent-700 bg-accent-50 dark:bg-accent-950/50'
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800'
                }`}
              >
                <input
                  type={question.type === 'single_choice' ? 'radio' : 'checkbox'}
                  name={`q${qIdx}_correct`}
                  checked={answer.isCorrect}
                  onChange={(e) => updateAnswer(qIdx, aIdx, 'isCorrect', e.target.checked)}
                  className="h-4 w-4 text-accent-600 flex-shrink-0 cursor-pointer"
                />
                <input
                  type="text"
                  value={answer.text}
                  onChange={(e) => updateAnswer(qIdx, aIdx, 'text', e.target.value)}
                  placeholder={t('courseContent.answerPlaceholder', { num: aIdx + 1 })}
                  className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                />
                {answer.isCorrect && (
                  <Badge color="green" className="flex-shrink-0">
                    {t('courseContent.correct')}
                  </Badge>
                )}
                {question.answers.length > 2 && (
                  <button
                    onClick={() => removeAnswer(qIdx, aIdx)}
                    className="text-danger-400 dark:text-danger-300 hover:text-danger-600 flex-shrink-0 text-lg leading-none transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => addAnswer(qIdx)}
              className="w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-sm text-slate-500 dark:text-slate-400 hover:border-primary-300 hover:text-primary-600 transition"
            >
              {t('courseContent.addAnswer')}
            </button>
          </div>
        </Card>
      ))}

      {/* Butoane jos */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={addQuestion}
          className="flex-1 border-2 border-dashed"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('courseContent.addQuestion')}
        </Button>
        {questions.length > 0 && (
          <Button
            variant="accent"
            size="lg"
            onClick={handleSave}
            loading={saving}
            className="flex-1"
          >
            {saving ? (
              t('common.saving')
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
                {t('courseContent.saveFinalTest', { count: questions.length })}
              </>
            )}
          </Button>
        )}
        {saved && (
          <span className="self-center text-sm text-green-600 dark:text-green-400 font-medium inline-flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t('courseContent.saved')}
          </span>
        )}
      </div>
    </div>
  );
};

const CourseDetailsTab = forwardRef(({ course, onSave, onDurationChange, onContinue }, ref) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const allowedTypes = getCreatableCourseTypes(user, !!user?.managesDepartment);
  const allow = (t) => allowedTypes.has(t);
  // Ref pe input-ul duratei + state pentru chenarul roșu când lipsește valoarea.
  // Folosite de parent la „Salvează și finalizează" — dacă durata e invalidă, scroll + highlight
  // direct pe câmp (în loc de toast generic).
  const durationInputRef = useRef(null);
  const [durationError, setDurationError] = useState(false);

  // Convert stored minutes to display value + unit
  const initTimeValue = (storedMinutes) => {
    if (!storedMinutes) return { value: '', unit: 'min' };
    if (storedMinutes >= 60 && storedMinutes % 60 === 0)
      return { value: (storedMinutes / 60).toString(), unit: 'ore' };
    return { value: storedMinutes.toString(), unit: 'min' };
  };
  // Durata de pe fișă se exprimă DOAR în ore (durata legală). Convertim minutele stocate în ore.
  const initDurationHours = (storedMinutes) => {
    if (!storedMinutes) return '';
    const h = storedMinutes / 60;
    return (Number.isInteger(h) ? h : parseFloat(h.toFixed(2))).toString();
  };
  const [form, setForm] = useState({
    title: course?.title || '',
    description: course?.description || '',
    type: course?.type || 'IIG_SSM',
    requiresTest: course?.requiresTest !== false,
    videoUrl: course?.videoUrl || '',
    durationValue: initDurationHours(course?.duration),
    durationUnit: 'ore',
    minTrainingValue: initTimeValue(course?.minTrainingTime).value,
    minTrainingUnit: initTimeValue(course?.minTrainingTime).unit,
    showCorrectAnswers: course?.showCorrectAnswers !== false,
    isTemplate: !!course?.isTemplate,
  });

  // Citim duration din DOM (via ref) ca SOURCE-OF-TRUTH defensiv în validate + save.
  // Anterior, dacă closure-ul state-ului era stale (rare, dar posibil în concurrent mode sau
  // la click rapid), valoarea tipa de user putea părea „nu e completată" în validate. DOM-ul
  // reflectă mereu ce vede userul.
  const liveDurationValue = () => {
    const fromDom = durationInputRef.current?.value;
    if (typeof fromDom === 'string' && fromDom.length > 0) return fromDom;
    return form.durationValue;
  };

  // Construiește payload-ul pentru save (folosit și prin requestSubmit nativ, și prin ref.save()).
  const buildPayload = () => {
    const toMinutes = (val, unit) => {
      if (!val) return null;
      const n = parseFloat(val);
      if (Number.isNaN(n) || n <= 0) return null;
      return Math.round(unit === 'ore' ? n * 60 : n);
    };
    return {
      title: form.title,
      description: form.description,
      type: form.type,
      requiresTest: form.requiresTest,
      showCorrectAnswers: form.showCorrectAnswers,
      videoUrl: form.videoUrl,
      duration: toMinutes(liveDurationValue(), form.durationUnit),
      minTrainingTime: toMinutes(form.minTrainingValue, form.minTrainingUnit),
      isTemplate: form.isTemplate,
    };
  };

  // Validare partajată (titlu + durată) — folosită ATÂT de „Salvează și finalizează" (prin ref),
  // CÂT și de butonul „Salvează și continuă" din interiorul tab-ului. Pune chenar roșu + scroll pe
  // câmpul invalid.
  const validateForm = () => {
    if (!form.title?.trim()) {
      showToast(t('courseContent.titleRequired'), 'error');
      return false;
    }
    // Citim direct din DOM ca defense in depth — `form.durationValue` ar trebui să fie sincron
    // cu input-ul, dar dacă cumva e stale (timing issue), valoarea live din input e adevărul.
    const liveVal = liveDurationValue();
    if (!liveVal || parseFloat(liveVal) <= 0 || Number.isNaN(parseFloat(liveVal))) {
      setDurationError(true);
      setTimeout(() => {
        durationInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        durationInputRef.current?.focus();
      }, 50);
      return false;
    }
    setDurationError(false);
    // Sincronizează state-ul cu valoarea live (dacă era cumva în decalaj)
    if (liveVal !== form.durationValue) {
      setForm((prev) => ({ ...prev, durationValue: liveVal }));
    }
    return true;
  };

  // „Salvează și continuă" — validează, salvează detaliile, apoi cere parent-ului să treacă pe
  // tab-ul „Materiale predate" (pasul firesc următor). Așa userul nu mai trebuie să deruleze sus.
  const handleSaveAndContinue = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await onSave(buildPayload());
      onContinue?.();
    } finally {
      setSaving(false);
    }
  };

  // Expune metode către parent: validate (cu scroll + highlight pe durata invalidă) + save async.
  // Folosite de fluxul „Salvează și finalizează" — nu mai există buton separat de save.
  useImperativeHandle(ref, () => ({
    validate: validateForm,
    save: async () => {
      setSaving(true);
      try {
        await onSave(buildPayload());
      } finally {
        setSaving(false);
      }
    },
    // Folosit de bara de completitudine din parent — click pe „Durata pe fișă" scrollează la
    // input și-i pune focus, ca userul să poată tasta direct.
    scrollToDuration: () => {
      durationInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      durationInputRef.current?.focus();
    },
  }));

  // Drept de publicare template — la fel ca în CourseForm
  const canPublishTemplate =
    ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) ||
    !!user?.isSSMInstructor ||
    !!user?.isCadruTehnicPSI;
  const [saving, setSaving] = useState(false);

  // Save direct nu mai e expus din form — utilizatorul folosește butonul „Salvează și
  // finalizează" din header-ul paginii. handleSubmit previne doar reload-ul accidental
  // pe Enter (form-ul nu mai are buton de submit propriu).
  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <Card className="max-w-2xl">
      <form id="course-details-form" onSubmit={handleSubmit} className="space-y-5">
        <Input
          label={t('courseContent.trainingTitleLabel') + ' *'}
          name="title"
          required
          value={form.title}
          onChange={handleChange}
          placeholder={t('courseContent.trainingTitlePlaceholder')}
        />

        <div>
          <label className="input-label">{t('courseContent.trainingTypeLabel') + ' *'}</label>
          <select
            name="type"
            required
            value={form.type}
            onChange={(e) => {
              const newType = e.target.value;
              // Testul obligatoriu rămâne pre-bifat DOAR pentru IIG. Pentru ILM/IP/suplimentare e
              // debifat implicit — testul devine opțional (bifabil manual dacă se dorește la ILM).
              const isIIG = newType === 'IIG_SSM' || newType === 'IIG_SU';
              setForm((prev) => ({ ...prev, type: newType, requiresTest: isIIG }));
            }}
            className="input"
          >
            <option value="">{t('courseContent.chooseCategory')}</option>
            <optgroup label="SSM">
              {allow('IIG_SSM') && <option value="IIG_SSM">{t('courses.type.IIG_SSM')}</option>}
              {allow('ILM_SSM') && <option value="ILM_SSM">{t('courses.type.ILM_SSM')}</option>}
              {allow('IP_SSM') && <option value="IP_SSM">{t('courses.type.IP_SSM')}</option>}
              {allow('SUPLIMENTARE_SSM') && (
                <option value="SUPLIMENTARE_SSM">{t('courses.type.SUPLIMENTARE_SSM')}</option>
              )}
            </optgroup>
            <optgroup label={t('courseContent.emergencySituations')}>
              {allow('IIG_SU') && <option value="IIG_SU">{t('courses.type.IIG_SU')}</option>}
              {allow('ILM_SU') && <option value="ILM_SU">{t('courses.type.ILM_SU')}</option>}
              {allow('IP_SU') && <option value="IP_SU">{t('courses.type.IP_SU')}</option>}
            </optgroup>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="edit-requiresTest"
            name="requiresTest"
            checked={form.requiresTest !== false}
            onChange={(e) => setForm((prev) => ({ ...prev, requiresTest: e.target.checked }))}
            className="w-4 h-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
          />
          <label
            htmlFor="edit-requiresTest"
            className="text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {t('courses.requiresTest')}
            <span className="block text-xs text-slate-400 dark:text-slate-400 font-normal">
              {t('courses.requiresTestHelp')}
            </span>
          </label>
        </div>

        {canPublishTemplate && (
          <div className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">
            <input
              type="checkbox"
              id="edit-isTemplate"
              checked={!!form.isTemplate}
              onChange={(e) => setForm((prev) => ({ ...prev, isTemplate: e.target.checked }))}
              className="w-4 h-4 text-purple-600 rounded border-purple-300 dark:border-purple-700 mt-0.5"
            />
            <label
              htmlFor="edit-isTemplate"
              className="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('courseContent.publishAsTemplate')}
              <span className="block text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">
                {t('courseContent.publishAsTemplateDesc')}
              </span>
            </label>
          </div>
        )}

        {form.requiresTest && (
          <div className="flex items-center gap-3 ml-7">
            <input
              type="checkbox"
              id="edit-showCorrectAnswers"
              checked={form.showCorrectAnswers !== false}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, showCorrectAnswers: e.target.checked }))
              }
              className="w-4 h-4 text-primary-600 rounded border-slate-300"
            />
            <label
              htmlFor="edit-showCorrectAnswers"
              className="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('courses.showCorrectAnswers')}
              <span className="block text-xs text-slate-400 dark:text-slate-400 font-normal">
                {t('courseContent.showCorrectAnswersHelp')}
              </span>
            </label>
          </div>
        )}

        <div>
          <label className="input-label">{t('courseContent.taughtMaterial')}</label>
          <textarea
            name="description"
            rows={3}
            value={form.description}
            onChange={handleChange}
            className="input"
            placeholder={t('courseContent.taughtMaterialPlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="input-label">{t('courses.duration') + ' *'}</label>
            <div className="flex gap-2">
              <input
                ref={durationInputRef}
                type="number"
                min="0.5"
                step="0.5"
                required
                value={form.durationValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((prev) => ({ ...prev, durationValue: val }));
                  if (durationError && val && parseFloat(val) > 0) setDurationError(false);
                  // Notifică parent-ul ca bara de completitudine să devină verde LIVE (nu doar
                  // după save). `course.duration` din parent rămâne stale până la save, deci
                  // folosim acest callback ca sursă live de adevăr.
                  if (onDurationChange) {
                    const parsed = parseFloat(val);
                    onDurationChange(!!val && !Number.isNaN(parsed) && parsed > 0);
                  }
                }}
                className={`input flex-1 ${durationError ? 'border-2 border-red-500 ring-2 ring-red-200 dark:ring-red-900/50' : ''}`}
                placeholder={t('courseContent.durationPlaceholder')}
              />
              <select
                value={form.durationUnit}
                onChange={(e) => setForm((prev) => ({ ...prev, durationUnit: e.target.value }))}
                className="input w-24"
              >
                <option value="ore">ore</option>
              </select>
            </div>
            {durationError ? (
              <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                {t('courseContent.durationErrorHint')}
              </p>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                {t('courses.durationHelp')}
              </p>
            )}
          </div>
          <div>
            <label className="input-label">{t('courses.minTrainingTime')}</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                step="1"
                value={form.minTrainingValue}
                onChange={(e) => setForm((prev) => ({ ...prev, minTrainingValue: e.target.value }))}
                className="input flex-1"
                placeholder={t('courseContent.minTimePlaceholder')}
              />
              <select
                value={form.minTrainingUnit}
                onChange={(e) => setForm((prev) => ({ ...prev, minTrainingUnit: e.target.value }))}
                className="input w-24"
              >
                <option value="min">min</option>
                <option value="ore">ore</option>
              </select>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
              {t('courses.minTrainingTimeHelp')}
            </p>
          </div>
        </div>

        {/* Buton de continuare în josul tab-ului — salvează detaliile și duce userul direct la
            „Materiale predate" (pasul următor), fără să mai deruleze sus. Pentru cineva care nu
            cunoaște platforma, e mult mai intuitiv decât butonul din header. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-800">
          <Button type="button" onClick={handleSaveAndContinue} loading={saving} size="lg">
            {saving ? t('common.saving') : t('courseContent.saveAndContinue')}
            {!saving && (
              <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            )}
          </Button>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            <Trans i18nKey="courseContent.saveAndContinueHint" components={{ b: <strong /> }} />
          </p>
        </div>
      </form>
    </Card>
  );
});

const MAX_MATERIALS_PER_KIND = 10;

// Iconițele pentru fiecare tip de material (PDF roșu, PPTX portocaliu, VIDEO violet).
const iconForKind = (kind) => {
  if (kind === 'PDF')
    return {
      color: 'text-red-500',
      d: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
    };
  if (kind === 'VIDEO')
    return {
      color: 'text-violet-500',
      d: 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
    };
  return {
    color: 'text-orange-500',
    d: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M16.5 3.75h.008v.008H16.5V3.75z',
  };
};

// MaterialsSection — definit TOP-LEVEL (nu interior la ModuleModal) ca să nu se re-monteze
// la fiecare render. Anterior: definit interior → React vedea o funcție nouă la fiecare render
// → unmount/remount → textarea pierdea focus după prima literă tastată.
const MaterialsSection = ({
  kind,
  label,
  accept,
  hint,
  items,
  pending,
  uploadingMaterial,
  savingModule,
  onAddMaterial,
  onDeleteMaterial,
  onDeletePending,
  onUpdateDescription,
  onUpdatePendingDescription,
}) => {
  const { t } = useTranslation();
  const total = items.length + pending.length;
  const atLimit = total >= MAX_MATERIALS_PER_KIND;
  const icon = iconForKind(kind);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="input-label !mb-0">{label}</label>
        <span
          className={`text-xs tabular-nums ${atLimit ? 'text-red-500' : 'text-slate-500 dark:text-slate-400'}`}
        >
          {total}/{MAX_MATERIALS_PER_KIND}
        </span>
      </div>
      {(items.length > 0 || pending.length > 0) && (
        <ul className="space-y-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <svg
                    className={`w-4 h-4 flex-shrink-0 ${icon.color}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon.d} />
                  </svg>
                  <span className="text-sm truncate">{m.originalName}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onDeleteMaterial(m.id)}
                  className="text-xs text-red-500 hover:text-red-700 flex-shrink-0"
                >
                  {t('common.delete')}
                </button>
              </div>
              <textarea
                value={m.description || ''}
                onChange={(e) => onUpdateDescription(m.id, e.target.value)}
                placeholder={t('courseContent.materialNotePlaceholder')}
                rows={2}
                className="input w-full text-xs"
              />
            </li>
          ))}
          {pending.map((p) => (
            <li
              key={p.tempId}
              className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <svg
                    className={`w-4 h-4 flex-shrink-0 ${icon.color}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon.d} />
                  </svg>
                  <span className="text-sm truncate">{p.originalName}</span>
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 flex-shrink-0">
                    {t('common.status.pending')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onDeletePending(p.tempId)}
                  disabled={uploadingMaterial || savingModule}
                  className="text-xs text-red-500 hover:text-red-700 flex-shrink-0 disabled:opacity-50"
                >
                  {t('common.delete')}
                </button>
              </div>
              <textarea
                value={p.description || ''}
                onChange={(e) => onUpdatePendingDescription(p.tempId, e.target.value)}
                placeholder={t('courseContent.materialNotePlaceholder')}
                rows={2}
                className="input w-full text-xs"
              />
            </li>
          ))}
        </ul>
      )}
      {!atLimit && (
        <input
          type="file"
          accept={accept}
          disabled={uploadingMaterial || savingModule}
          onChange={(e) => {
            const f = e.target.files[0];
            if (f) onAddMaterial(f, kind);
            e.target.value = '';
          }}
          className="input"
        />
      )}
      {atLimit && (
        <p className="text-xs text-amber-600 dark:text-amber-300">
          {t('courseContent.materialLimitReached', { max: MAX_MATERIALS_PER_KIND })}
        </p>
      )}
      <p className="text-xs text-slate-400 dark:text-slate-400">{hint}</p>
    </div>
  );
};

const ModuleModal = ({ module, onSave, onDone, onClose }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [title, setTitle] = useState(module?.title || '');
  const [content, setContent] = useState(module?.content || '');
  const [videoUrl] = useState(module?.videoUrl || '');
  const [selectedFile] = useState(null);
  // Materialele deja salvate pe server (la editare apar din `module?.materials`).
  const [materials, setMaterials] = useState(module?.materials || []);
  // Materialele alese local care încă nu au fost trimise pe server
  // (folosite la „Material nou" cât timp modulul nu există încă, dar și la editare
  // ca să acumulezi mai multe înainte de salvare). Structura: { tempId, kind, file, originalName }
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [savingModule, setSavingModule] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const handleAddMaterial = (file, kind) => {
    if (!file) return;
    setPendingFiles((prev) => [
      ...prev,
      {
        tempId: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        kind,
        file,
        originalName: file.name,
      },
    ]);
  };

  const handleDeletePending = (tempId) => {
    setPendingFiles((prev) => prev.filter((p) => p.tempId !== tempId));
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await api.delete(`/modules/materials/${materialId}`);
      setMaterials((prev) => prev.filter((m) => m.id !== materialId));
    } catch (err) {
      showToast(t('courseContent.deleteMaterialError'), 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingModule(true);
    try {
      let finalVideoUrl = videoUrl;

      if (selectedFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('video', selectedFile);
        const response = await api.post('/modules/upload-video', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        finalVideoUrl = response.data.videoUrl;
      }

      const savedModule = await onSave({
        title,
        content,
        videoUrl: finalVideoUrl || null,
        orderIndex: module?.orderIndex || 0,
      });

      const targetId = savedModule?.id || module?.id;
      if (pendingFiles.length > 0 && targetId) {
        setUploadingMaterial(true);
        setUploadProgress({ done: 0, total: pendingFiles.length });
        // Upload paralel cu limită de concurență (max 3 deodată) — fișierele PPTX necesită
        // conversie LibreOffice pe server, iar serializarea anterioară (`for...await`) făcea
        // ca un upload de 5 PPTX-uri să dureze 1-2 minute. Cu paralelism limitat scade
        // semnificativ timpul total, fără să suprasolicităm serverul.
        const CONCURRENCY = 3;
        let doneCount = 0;
        const uploadOne = async (p) => {
          const fd = new FormData();
          fd.append('file', p.file);
          // Endpoint diferit pentru VIDEO (storage /uploads/videos, fără conversie PPT).
          const endpoint =
            p.kind === 'VIDEO'
              ? `/modules/${targetId}/video-materials`
              : `/modules/${targetId}/materials`;
          try {
            const res = await api.post(endpoint, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            // Dacă userul a setat o descriere pe item-ul pending, o trimitem prin PATCH după upload.
            if (p.description && res.data?.id) {
              try {
                await api.patch(`/modules/materials/${res.data.id}`, {
                  description: p.description,
                });
              } catch (_) {
                /* descrierea poate fi setată ulterior din editor */
              }
            }
          } catch (err) {
            showToast(
              `${p.originalName}: ${err.response?.data?.error || t('courseContent.uploadError')}`,
              'error'
            );
          } finally {
            doneCount++;
            setUploadProgress({ done: doneCount, total: pendingFiles.length });
          }
        };
        // Worker simplu: fiecare worker scoate pe rând un file din coadă și-l urcă.
        const queue = [...pendingFiles];
        const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
          while (queue.length > 0) {
            const next = queue.shift();
            if (next) await uploadOne(next);
          }
        });
        await Promise.all(workers);
        setUploadingMaterial(false);
        setPendingFiles([]);
      }

      if (onDone) await onDone();
      else onClose();
    } catch (err) {
      showToast(err.response?.data?.error || t('courseContent.saveMaterialError'), 'error');
    } finally {
      setSavingModule(false);
    }
  };

  const pdfMaterials = materials.filter((m) => m.kind === 'PDF');
  const pptMaterials = materials.filter((m) => m.kind === 'POWERPOINT');
  const videoMaterials = materials.filter((m) => m.kind === 'VIDEO');
  const pdfPending = pendingFiles.filter((p) => p.kind === 'PDF');
  const pptPending = pendingFiles.filter((p) => p.kind === 'POWERPOINT');
  const videoPending = pendingFiles.filter((p) => p.kind === 'VIDEO');

  // Descrierea fiecărui material existent — modificată inline în editor și PATCH la backend.
  // Debounce 400ms pe PATCH ca să nu trimitem request pe fiecare keystroke. Setarea de state
  // (optimist) e instantă, doar request-ul de salvare e amânat.
  const descriptionPatchTimers = useRef({});
  const handleUpdateDescription = (materialId, description) => {
    setMaterials((prev) => prev.map((m) => (m.id === materialId ? { ...m, description } : m)));
    if (descriptionPatchTimers.current[materialId]) {
      clearTimeout(descriptionPatchTimers.current[materialId]);
    }
    descriptionPatchTimers.current[materialId] = setTimeout(() => {
      api
        .patch(`/modules/materials/${materialId}`, { description })
        .catch(() => showToast(t('courseContent.saveDescriptionError'), 'error'));
    }, 400);
  };
  // Pentru materialele PENDING (încă neuploadate) — descriere stocată local, trimisă la upload.
  const handleUpdatePendingDescription = (tempId, description) => {
    setPendingFiles((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, description } : p)));
  };

  // MaterialsSection și iconForKind sunt definite TOP-LEVEL (deasupra ModuleModal) — nu trebuie
  // re-create la fiecare render. Tot ce le trebuie e pasat ca props.

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={module ? t('courseContent.editMaterialTitle') : t('courseContent.newMaterialTitle')}
      size="2xl"
      footer={
        <div className="flex gap-3 justify-end w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={savingModule || uploadingMaterial}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="module-form"
            loading={savingModule || uploadingMaterial}
            disabled={uploadingMaterial}
          >
            {uploadingMaterial
              ? t('courseContent.uploadingMaterials', {
                  done: uploadProgress.done,
                  total: uploadProgress.total,
                })
              : savingModule
                ? t('common.saving')
                : t('common.save')}
          </Button>
        </div>
      }
    >
      <form id="module-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('courseContent.titleField') + ' *'}
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('courseContent.moduleTitlePlaceholder')}
        />
        {/* Secțiunea „Video legacy" (un singur video direct pe Module) — scoasă din UI.
            Toate videoclipurile se adaugă acum prin secțiunea Multi-video de mai jos (ca PDF-urile).
            Câmpul `Module.videoUrl` rămâne în schema pentru backward-compatibility cu modulele
            create anterior, dar nu mai e editabil din editor. */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MaterialsSection
            kind="PDF"
            label={t('courseContent.pdfsLabel')}
            accept="application/pdf"
            hint={t('courseContent.pdfHint')}
            items={pdfMaterials}
            pending={pdfPending}
            uploadingMaterial={uploadingMaterial}
            savingModule={savingModule}
            onAddMaterial={handleAddMaterial}
            onDeleteMaterial={handleDeleteMaterial}
            onDeletePending={handleDeletePending}
            onUpdateDescription={handleUpdateDescription}
            onUpdatePendingDescription={handleUpdatePendingDescription}
          />
          <MaterialsSection
            kind="POWERPOINT"
            label={t('courseContent.powerpointsLabel')}
            accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            hint={t('courseContent.powerpointHint')}
            items={pptMaterials}
            pending={pptPending}
            uploadingMaterial={uploadingMaterial}
            savingModule={savingModule}
            onAddMaterial={handleAddMaterial}
            onDeleteMaterial={handleDeleteMaterial}
            onDeletePending={handleDeletePending}
            onUpdateDescription={handleUpdateDescription}
            onUpdatePendingDescription={handleUpdatePendingDescription}
          />
        </div>

        <MaterialsSection
          kind="VIDEO"
          label={t('courseContent.videosLabel')}
          accept="video/mp4,video/webm,video/ogg,video/quicktime"
          hint={t('courseContent.videoHint')}
          items={videoMaterials}
          pending={videoPending}
          uploadingMaterial={uploadingMaterial}
          savingModule={savingModule}
          onAddMaterial={handleAddMaterial}
          onDeleteMaterial={handleDeleteMaterial}
          onDeletePending={handleDeletePending}
          onUpdateDescription={handleUpdateDescription}
          onUpdatePendingDescription={handleUpdatePendingDescription}
        />

        <div>
          <label className="input-label">{t('courseContent.contentLabel')}</label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder={t('courseContent.contentPlaceholder')}
          />
        </div>
      </form>
    </Modal>
  );
};

const QuestionModal = ({ question, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    text: question?.text || '',
    type: question?.type || 'single_choice',
    orderIndex: question?.orderIndex || 0,
  });

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={
        question
          ? t('courseContent.editQuestionModalTitle')
          : t('courseContent.newQuestionModalTitle')
      }
      size="lg"
    >
      <Alert type="warning" className="mb-4">
        {t('courseContent.questionCheckNote')}
      </Alert>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="input-label">{t('courseContent.questionTextLabel') + ' *'}</label>
          <textarea
            required
            rows={3}
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            className="input"
            placeholder={t('courseContent.questionTextPlaceholder')}
          />
        </div>
        <Select
          label={t('courseContent.typeLabel')}
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          options={[
            { value: 'single_choice', label: t('courseContent.simpleChoice') },
            { value: 'multiple_choice', label: t('courseContent.multipleChoiceOpt') },
          ]}
        />
        <div className="flex gap-4">
          <Button type="submit" size="lg" className="flex-1">
            {t('common.save')}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const AnswerModal = ({ answer, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    text: answer?.text || '',
    isCorrect: answer?.isCorrect || false,
    orderIndex: answer?.orderIndex || 0,
  });

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={answer ? t('courseContent.editAnswerTitle') : t('courseContent.newAnswerTitle')}
      size="lg"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(formData);
        }}
        className="space-y-4"
      >
        <Input
          label={t('courseContent.answerTextLabel') + ' *'}
          required
          value={formData.text}
          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
          placeholder={t('courseContent.answerTextPlaceholder')}
        />
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isCorrect}
            onChange={(e) => setFormData({ ...formData, isCorrect: e.target.checked })}
            className="h-5 w-5 text-accent-600 focus:ring-accent-500 border-slate-300 dark:border-slate-700 rounded"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <svg
              className="w-4 h-4 text-accent-600 dark:text-accent-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t('courseContent.thisIsCorrect')}
          </span>
        </label>
        <div className="flex gap-4">
          <Button type="submit" size="lg" className="flex-1">
            {t('common.save')}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CourseContent;
